import { Component } from '@angular/core';
import { EventEmitter, Output  } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { AbstractControl, AsyncValidatorFn } from '@angular/forms';
import { Validators, ValidationErrors } from '@angular/forms';

import { languageService, languageSupport } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';
import { loginService } from '@unpispas/upp-data';

import type { LoginCredentials } from '../login-page/upp-login';

@Component({
  selector: 'upp-registry-page',
  templateUrl: './upp-registry.html',
  styleUrls: [
    './upp-registry.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UppRegistryPageComponent extends languageSupport {
    @Output() Accept = new EventEmitter<LoginCredentials>();
    @Output() Cancel = new EventEmitter<void>();

    constructor(
        private lang: languageService,
        private change: ChangeDetectorRef,
        private login: loginService,
        public view: viewService
    ){
        super(lang, change);
    }

    private checkAvailableUser(): AsyncValidatorFn {
        return (control: AbstractControl): Promise<{ available: boolean } | null> => {
            if (!control.value) {
                return Promise.resolve(null);
            }
      
            return this.login.Available(control.value)
            .then(
            (data) => {
                if (!data && control.valid){
                    return { available: true };
                }
                return null;
            })
            .catch(() => {
                return null;
            }) 
        };
    }

    private checkLicenseAgree(control: AbstractControl): ValidationErrors | null {
        const _agree: boolean = control.value;
        if (!_agree){
            return { 'license': true }; 
        }

        return null;
    }

    private checkConfirmPassword(group: AbstractControl): ValidationErrors | null {
        const _password: AbstractControl | null = group.get('password');
        const _confirm: AbstractControl | null = group.get('confirm');
    
        if (_password && _confirm){
            _confirm.setErrors(null);
            if (_password.value !== _confirm.value){
                _confirm.setErrors({ 'nomatch': true });
            }
        }
    
        return null;  
    }

    private _registryForm: FormGroup | null = null;
    get RegistryForm() {
        if (this._registryForm == null){
            this._registryForm = new FormGroup({
                username: new FormControl(null, {
                    validators: [
                        Validators.required,
                        Validators.email
                    ],
                    asyncValidators: [
                        this.checkAvailableUser()
                    ]
                }),
                password: new FormControl(null, [
                    Validators.required,
                    Validators.minLength(8)                        
                ]),
                confirm: new FormControl(null, [
                    Validators.required,
                    Validators.minLength(8)                        
                ]),
                licence: new FormControl(false, [
                    Validators.required,
                    this.checkLicenseAgree.bind(this)
                ])}, 
            this.checkConfirmPassword);
        }

        return this._registryForm;
    }  
    
    CloseView(): void {
        this.RegistryForm.reset();
        this.Cancel.next();
    }

    async DoRegister(): Promise <void> {
        const _username: string | null = this.RegistryForm.get('username')?.value || null;
        const _password: string | null = this.RegistryForm.get('password')?.value || null;

        if (!_username || !_password){
            return;
        }

        const _success = await this.login.DoRegister(_username, _password);
        if (!_success){
            return;
        }

        const _credentials: LoginCredentials = {
            username: _username,
            password: _password
        };

        this.RegistryForm.reset();
        this.Accept.next(_credentials);
    }

    ShowLicence(event: Event){
        event.preventDefault();
        window.open("https://unpispas.com/upp-home/html/license/license.html", "_blank");
        return false; 
    }
}
